// Publicar un lanzamiento desde el chat — fase D de
// ADR-2026-09-21-publicar-un-lanzamiento-desde-el-chat.
//
// Un lanzamiento es una tanda de productos con su ficha y sus fotos, armada en la
// pantalla de Lanzamientos. Publicarlo es la operación más grande que tiene Atlas sobre
// la tienda de alguien: **crea productos nuevos a la venta**, crea categorías que no
// existían y sube todas las fotos de una.
//
// Por eso acá no hay ninguna decisión propia: se elige el lanzamiento, se cuenta con
// todas las letras lo que va a pasar, y se publica lo mismo que publicaría el botón de
// la pantalla, con los mismos pasos (`publicarLanzamiento`). Lo único que esta capa
// agrega es lo que necesita una conversación: encontrar el lanzamiento por su nombre,
// explicar lo que falta cuando falta, y la confirmación verificable.
//
// Publica en las tiendas que eligió el lanzamiento: Tiendanube, Mercado Libre o las dos
// (ADR-2026-09-30-publicar-en-mercado-libre-desde-el-chat). Es UNA herramienta para las dos a
// propósito: dos casi iguales (una por tienda) son las que el modelo confunde. El archivo
// conserva el nombre de cuando sólo publicaba en Tiendanube.
import type { HerramientaChat } from './tipos';
import { anotarAviso, consumirAviso, puedeActuar } from './confirmacionVerificable';
import type {
  Lanzamiento,
  LanzamientoDetalle,
  ProblemaDePublicacion,
} from '../pages/lanzamientos/lanzamientosApi';
import {
  frenoDeMl,
  productosParaMl,
  publicacionesDe,
  yaPublicadoEnMl,
  YA_PUBLICANDO,
  type ResultadoDePublicar,
  type ResultadoDePublicarMl,
} from '../pages/lanzamientos/publicarLanzamiento';
import { destinosDe, type CanalLanzamiento } from '../pages/lanzamientos/canales';
import { NOMBRE_CONECTAR_MERCADOLIBRE } from './mercadoLibreEnElChat';
import { avisarCambioDeLanzamiento } from '../pages/lanzamientos/cambioDeLanzamiento';

export const NOMBRE_PUBLICAR_LANZAMIENTO = 'publicar_lanzamiento';

export const HERRAMIENTA_PUBLICAR_LANZAMIENTO: HerramientaChat = {
  name: NOMBRE_PUBLICAR_LANZAMIENTO,
  description:
    'Publica un lanzamiento entero en las tiendas que tiene elegidas: Tiendanube, Mercado Libre o las dos. En Tiendanube crea los productos que faltan, las categorías y sube las fotos; en Mercado Libre crea una publicación por color y talle. Usala cuando pida publicar, lanzar o mandar un lanzamiento a su tienda o a Mercado Libre. Sin `lanzamiento` te devuelvo la lista para que elija. Es la operación más grande que hay y NO se deshace desde Atlas: lo nuevo queda a la venta. La primera llamada te devuelve qué se va a publicar y no toca nada; contáselo, esperá su respuesta, y recién en tu turno siguiente volvé a llamarla con confirmado. Si a alguna ficha le falta algo, te digo qué y no publico nada: eso se completa en la pantalla de Lanzamientos.',
  input_schema: {
    type: 'object',
    properties: {
      lanzamiento: {
        type: 'string',
        description: 'El lanzamiento, por nombre o por id. Omitilo para ver la lista.',
      },
      confirmado: {
        type: 'boolean',
        description:
          'Sólo true después de que el usuario dijo que sí, en un mensaje posterior al aviso.',
      },
    },
  },
};

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export type EleccionDeLanzamiento =
  | { tipo: 'uno'; lanzamiento: Lanzamiento }
  | { tipo: 'varios'; opciones: Lanzamiento[] }
  | { tipo: 'ninguno' };

/** Igual que con los productos: id directo, después nombre exacto, después contenido. */
export function elegirLanzamiento(pedido: string, lista: Lanzamiento[]): EleccionDeLanzamiento {
  const porId = lista.find((l) => l.id === pedido);
  if (porId) return { tipo: 'uno', lanzamiento: porId };
  const buscado = normalizar(pedido);
  if (!buscado) return { tipo: 'ninguno' };
  const exactos = lista.filter((l) => normalizar(l.nombre) === buscado);
  const candidatos =
    exactos.length > 0 ? exactos : lista.filter((l) => normalizar(l.nombre).includes(buscado));
  if (candidatos.length === 0) return { tipo: 'ninguno' };
  if (candidatos.length > 1) return { tipo: 'varios', opciones: candidatos };
  return { tipo: 'uno', lanzamiento: candidatos[0] };
}

/** Lo que le falta a las fichas, dicho de forma que se pueda arreglar. */
export function textoDeProblemas(problemas: ProblemaDePublicacion[]): string {
  const primeros = problemas.slice(0, 5).map((p) => `"${p.nombre}": ${p.motivo}`);
  const resto = problemas.length - primeros.length;
  return primeros.join('; ') + (resto > 0 ? `, y ${resto} más` : '');
}

export interface DependenciasDePublicarDesdeElChat {
  /** Los lanzamientos de la marca. */
  lista: () => Promise<Lanzamiento[]>;
  /** La ficha completa de uno. */
  detalle: (id: string) => Promise<LanzamientoDetalle | null>;
  /** Publica en Tiendanube: son los mismos pasos que el botón de la pantalla. */
  publicar: (detalle: LanzamientoDetalle) => Promise<ResultadoDePublicar>;
  /**
   * Mercado Libre, sólo si la marca lo tiene disponible (backend prendido y dev tester). Sin esto,
   * un lanzamiento que va a Mercado Libre no se publica allá desde el chat.
   */
  mercadoLibre?: {
    /** Leída en el momento y con la marca DEL LANZAMIENTO (puede no ser la activa). */
    cuentaConectada: (detalle: LanzamientoDetalle) => Promise<boolean>;
    publicar: (detalle: LanzamientoDetalle) => Promise<ResultadoDePublicarMl>;
  };
}

type TiendaDelChat = 'tiendanube' | 'mercadolibre';

/** Las tiendas del lanzamiento en las que el chat puede publicar. */
export function tiendasAPublicar(
  l: { canal: CanalLanzamiento; destinos?: readonly string[] | null },
  conMercadoLibre: boolean,
): TiendaDelChat[] {
  return destinosDe(l).filter(
    (d): d is TiendaDelChat => d === 'tiendanube' || (d === 'mercadolibre' && conMercadoLibre),
  );
}

const lista = (nombres: string[], tope = 10): string => {
  const visibles = nombres.slice(0, tope);
  const resto = nombres.length - visibles.length;
  return visibles.map((n) => `"${n}"`).join(', ') + (resto > 0 ? ` y ${resto} más` : '');
};

const cuenta = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** "Clásica", "Premium" o la mezcla, según lo elegido en cada ficha. */
function tiposDePublicacion(detalle: LanzamientoDetalle): string {
  const productos = productosParaMl(detalle);
  const premium = productos.filter((p) => p.mercadolibre?.tipo_publicacion === 'gold_pro').length;
  if (premium === 0) return 'Clásica';
  if (premium === productos.length) return 'Premium';
  return `${cuenta(productos.length - premium, 'producto', 'productos')} en Clásica y ${premium} en Premium`;
}

/**
 * Una firma corta de los términos de cada producto (precio, variantes con su precio, tipo de publicación):
 * si cambian entre el aviso y el sí, el sí viejo no alcanza (review Greptile #156).
 */
export function firmaDeTerminos(detalle: LanzamientoDetalle): string {
  const texto = detalle.productos_
    .map((p) =>
      [
        p.id,
        p.precio ?? '',
        (p.variantes ?? []).map((v) => `${v.id}=${v.precio ?? ''}`).join(','),
        p.mercadolibre?.tipo_publicacion ?? '',
      ].join('/'),
    )
    .join('|');
  // djb2: alcanza para notar un cambio; no es seguridad.
  let h = 5381;
  for (let i = 0; i < texto.length; i++) h = ((h << 5) + h + texto.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Lo que frena a Mercado Libre, dicho para que el modelo se lo cuente a la persona. */
function textoDeFrenoMl(freno: Exclude<ResultadoDePublicarMl, { motivo: 'lanzado' }>, ambas: boolean): string {
  const tampoco = ambas ? ' Tampoco publiqué en Tiendanube: el lanzamiento sale junto.' : '';
  switch (freno.motivo) {
    case 'sin-cuenta':
      return `No publiqué nada: el lanzamiento va a Mercado Libre y la cuenta no está conectada.${tampoco} Mostrale la tarjeta con \`${NOMBRE_CONECTAR_MERCADOLIBRE}\`; la conecta el dueño de la cuenta.`;
    case 'falta':
      return `No publiqué nada: para Mercado Libre, en "${freno.producto.nombre}" ${freno.texto}.${tampoco} Eso se completa en la pantalla de Lanzamientos, en la sección Mercado Libre de la ficha de ese producto (la categoría, los datos que pide —con "Completar con IA"— y la guía de talles). Decíselo así.`;
    case 'sin-fotos':
      return 'No publiqué nada: ningún producto tiene fotos para Mercado Libre.';
    case 'rechazado':
      return `Mercado Libre no lo aceptó (${freno.texto}): ahí no se creó nada.`;
  }
}

export async function resolverPublicarLanzamiento(
  input: unknown,
  deps: DependenciasDePublicarDesdeElChat,
  ctx: { turno: number | null; ultimoMensaje?: string },
): Promise<{ __texto: string }> {
  const texto = (t: string) => ({ __texto: t });
  const pedido = (input ?? {}) as { lanzamiento?: unknown; confirmado?: unknown };
  const pedidoNombre = typeof pedido.lanzamiento === 'string' ? pedido.lanzamiento.trim() : '';
  const conMl = !!deps.mercadoLibre;

  // SÓLO los que van a una tienda donde el chat publica (Tiendanube, y Mercado Libre si está
  // disponible). Un lanzamiento de redes o de VTEX que se colara acá terminaría creando sus
  // productos en el destino equivocado, y eso no se deshace desde Atlas (review Greptile #127).
  const todos = (await deps.lista()).filter((l) => tiendasAPublicar(l, conMl).length > 0);
  const dondeSeArma = conMl ? 'Tiendanube o Mercado Libre' : 'Tiendanube';
  if (todos.length === 0) {
    return texto(
      `No tiene ningún lanzamiento de ${dondeSeArma} armado. Los lanzamientos se arman en la pantalla de Lanzamientos, eligiendo ${dondeSeArma} como destino: ahí junta los productos con su ficha y sus fotos, y después se publican de una. Si tiene alguno de otro destino (redes, VTEX), ese no se publica por acá.`,
    );
  }

  if (!pedidoNombre) {
    return texto(
      `Tiene ${cuenta(todos.length, 'lanzamiento', 'lanzamientos')}: ${lista(
        todos.map((l) => `${l.nombre} (${l.estado}, ${cuenta(l.productos, 'producto', 'productos')})`),
      )}. Preguntale cuál quiere publicar y volvé a llamarme con ese nombre. No publiqué nada.`,
    );
  }

  const eleccion = elegirLanzamiento(pedidoNombre, todos);
  if (eleccion.tipo === 'ninguno') {
    return texto(
      `No encontré el lanzamiento "${pedidoNombre}". Los que tiene son: ${lista(todos.map((l) => l.nombre))}. Preguntale cuál.`,
    );
  }
  if (eleccion.tipo === 'varios') {
    return texto(
      `Por "${pedidoNombre}" hay varios: ${lista(eleccion.opciones.map((l) => l.nombre))}. Preguntale cuál y volvé a llamarme con el nombre exacto. No publiqué nada.`,
    );
  }

  const detalle = await deps.detalle(eleccion.lanzamiento.id);
  if (!detalle) {
    return texto('No se pudo leer ese lanzamiento. Decíselo y ofrecé reintentar; no publiqué nada.');
  }
  // Cinturón y tirantes: el id pudo venir del modelo, no de la lista de arriba.
  const tiendas = tiendasAPublicar(detalle, conMl);
  if (tiendas.length === 0) {
    return texto(
      `No publiqué nada: "${detalle.nombre}" es un lanzamiento de ${detalle.canal}, no de ${dondeSeArma}. Publicarlo acá crearía sus productos en una tienda que no es su destino. Decíselo y, si quiere, que lo abra en la pantalla de Lanzamientos.`,
    );
  }
  if (detalle.productos_.length === 0) {
    return texto(
      `"${detalle.nombre}" no tiene ningún producto cargado, así que no hay nada para publicar. Se agregan desde la pantalla de Lanzamientos.`,
    );
  }

  const vaTn = tiendas.includes('tiendanube');
  const ml = tiendas.includes('mercadolibre') ? deps.mercadoLibre! : null;
  const ambas = vaTn && !!ml;

  // Lo de Mercado Libre se revisa ANTES de avisar: si falta algo, la persona se entera ahora y
  // no después de decir que sí. Mismo freno que el botón de la pantalla (`frenoDeMl`).
  if (ml) {
    const freno = frenoDeMl(detalle, await ml.cuentaConectada(detalle));
    if (freno) return texto(textoDeFrenoMl(freno, ambas));
  }

  // Ya publicado: volver a publicar no rompe nada (el worker reusa el producto creado, y en
  // Mercado Libre se busca el SKU antes de crear), pero la persona tiene que saber que lo hace
  // de nuevo.
  const yaEstuvo = detalle.estado === 'publicado' || detalle.estado === 'publicando';

  const aCrear = detalle.productos_.filter((p) => !p.tnProductId).length;
  const fotos = detalle.productos_.reduce((n, p) => n + p.imagenes.length, 0);
  const paraMl = ml ? productosParaMl(detalle) : [];
  const publicacionesMl = paraMl.filter((p) => !yaPublicadoEnMl(p)).reduce((n, p) => n + publicacionesDe(p), 0);
  const yaEnMl = paraMl.filter(yaPublicadoEnMl).length;
  // La clave lleva lo que el aviso le cuenta a la persona: si cambia entre el aviso y su sí (otro tipo de
  // publicación, más publicaciones), el sí viejo no alcanza y se vuelve a avisar (review Greptile #156).
  const clave = [
    detalle.id,
    tiendas.join('+'),
    detalle.productos_.length,
    fotos,
    aCrear,
    publicacionesMl,
    yaEnMl,
    ml ? tiposDePublicacion(detalle) : '',
    firmaDeTerminos(detalle),
  ].join(':');
  const confirmado = pedido.confirmado === true;

  if (
    !puedeActuar({
      accion: NOMBRE_PUBLICAR_LANZAMIENTO,
      clave,
      confirmado,
      turno: ctx.turno,
      ultimoMensaje: ctx.ultimoMensaje,
    })
  ) {
    anotarAviso(NOMBRE_PUBLICAR_LANZAMIENTO, clave, ctx.turno);
    const partes: string[] = [];
    if (vaTn) {
      const nuevos =
        aCrear > 0
          ? ` ${cuenta(aCrear, 'producto NUEVO se va a crear', 'productos NUEVOS se van a crear')} en su tienda, a la venta`
          : ' No se crea ningún producto nuevo: todos ya existen allá';
      partes.push(
        `${ambas ? ' En Tiendanube:' : ''}${nuevos}. También se crean las categorías que falten.`,
      );
    }
    if (ml) {
      partes.push(
        publicacionesMl > 0
          ? ` En Mercado Libre se van a crear ${cuenta(publicacionesMl, 'publicación NUEVA', 'publicaciones NUEVAS')} (una por cada color y talle, en ${tiposDePublicacion(detalle)}), a la venta apenas Mercado Libre las acepta. Mercado Libre cobra su comisión por cada venta, más alta en Premium.`
          : ' En Mercado Libre no se crea ninguna publicación nueva.',
      );
      if (yaEnMl > 0) {
        partes.push(` ${cuenta(yaEnMl, 'producto ya está publicado', 'productos ya están publicados')} en Mercado Libre: no se duplica${yaEnMl === 1 ? '' : 'n'}.`);
      }
    }
    const deshacer = ambas
      ? ' Desde Atlas no se puede deshacer: sacar algo es entrar a Tiendanube o a Mercado Libre.'
      : vaTn
        ? ' Desde Atlas no se puede deshacer: sacar un producto de la tienda es entrar a Tiendanube.'
        : ' Desde Atlas no se puede deshacer: pausar o borrar una publicación es entrar a Mercado Libre.';
    const repetido = yaEstuvo
      ? ` OJO: este lanzamiento ya se publicó antes (${detalle.estado}); volver a publicarlo sube de nuevo lo que falte, no duplica los productos.`
      : '';
    return texto(
      `NO PUBLIQUÉ NADA TODAVÍA. "${detalle.nombre}" tiene ${cuenta(detalle.productos_.length, 'producto', 'productos')} y ${cuenta(fotos, 'foto', 'fotos')}.${partes.join('')}${deshacer}${repetido} Contáselo con esas palabras y preguntale si publica. Si en su próximo mensaje dice que sí, volvé a llamarme con confirmado en true.`,
    );
  }

  // Un permiso, una publicación: publicar de nuevo tiene que volver a avisar, porque
  // vuelve a escribir en la tienda (review Greptile #127, segunda pasada).
  consumirAviso(NOMBRE_PUBLICAR_LANZAMIENTO);

  // Si una tienda no puede, la otra sigue (igual que el botón de la pantalla).
  const salio: string[] = [];
  const noSalio: string[] = [];
  if (vaTn) {
    const r = await deps.publicar(detalle);
    if (r.estado === 'faltan') {
      return texto(
        `No publiqué nada porque falta completar ${cuenta(r.problemas.length, 'ficha', 'fichas')}: ${textoDeProblemas(r.problemas)}. Eso se completa en la pantalla de Lanzamientos, en la ficha de cada producto. Decíselo así.`,
      );
    }
    if (r.estado === 'lanzado') {
      const creados =
        (r.seCrean > 0 ? ` Se están creando ${cuenta(r.seCrean, 'producto nuevo', 'productos nuevos')} allá` : '') +
        // ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento
        (r.seEditan > 0
          ? `${r.seCrean > 0 ? ' y' : ''} se les cambia la ficha a ${cuenta(r.seEditan, 'producto que ya estaba', 'productos que ya estaban')}`
          : '');
      salio.push(
        ambas
          ? ` En Tiendanube: ${cuenta(r.productos, 'producto', 'productos')} con ${cuenta(r.imagenes, 'foto', 'fotos')}.${creados}.`
          : ` ${cuenta(r.productos, 'producto', 'productos')} con ${cuenta(r.imagenes, 'foto', 'fotos')}.${creados}.`,
      );
    } else if (!ml) {
      if (r.estado === 'sin-tienda') {
        return texto(
          'No publiqué nada: la tienda no está conectada en este momento. Pedile que la conecte desde Mi marca y volvé a intentar.',
        );
      }
      if (r.estado === 'sin-fotos') {
        return texto(
          'No publiqué nada: no se pudo preparar ninguna foto para subir. Decíselo y ofrecé reintentar.',
        );
      }
      if (r.estado === 'sin-cambios') {
        return texto(
          'No publiqué nada: esos productos ya están en la tienda igual que en el lanzamiento y no tienen fotos nuevas. Decíselo: si quiere cambiar algo, que lo edite en la ficha del lanzamiento.',
        );
      }
      if (r.estado === 'sin-lectura') {
        return texto(
          'No publiqué nada: no pude leer cómo están esos productos en la tienda, y sin eso no sé qué cambiarles. Decíselo y ofrecé reintentar en un rato.',
        );
      }
      return texto(
        r.motivo === 'tienda_no_conectada'
          ? 'No publiqué nada: la tienda dejó de estar conectada. Pedile que la vuelva a conectar desde Mi marca.'
          : r.motivo === YA_PUBLICANDO
            ? 'No lo mandé de nuevo: ese lanzamiento ya se está publicando en Tiendanube (lo pidieron antes, desde la pantalla o desde acá). Mandarlo otra vez duplicaría productos. Decile que espere a que termine; el avance se ve en la pantalla de Lanzamientos.'
            : 'No se pudo publicar. Aclarale que en su tienda NO se creó ni se subió nada, y ofrecé reintentar.',
      );
    } else {
      noSalio.push(
        r.estado === 'sin-tienda' || (r.estado === 'rechazado' && r.motivo === 'tienda_no_conectada')
          ? ' En Tiendanube NO se publicó: la tienda no está conectada (se conecta desde Mi marca).'
          : r.estado === 'sin-fotos'
            ? ' En Tiendanube NO se publicó: no se pudo preparar ninguna foto.'
            : r.estado === 'sin-cambios'
              ? ' En Tiendanube no había nada para cambiar: ya están igual que en el lanzamiento.'
              : r.estado === 'sin-lectura'
                ? ' En Tiendanube NO se publicó: no pude leer cómo están los productos allá.'
                : r.estado === 'rechazado' && r.motivo === YA_PUBLICANDO
              ? ' En Tiendanube no lo mandé de nuevo: ya se está publicando allá.'
              : ' En Tiendanube NO se publicó: no lo aceptó, y ahí no se creó ni se subió nada.',
      );
    }
  }
  if (ml) {
    const r = await ml.publicar(detalle);
    if (r.motivo === 'lanzado') {
      salio.push(
        ` En Mercado Libre se están creando ${cuenta(r.publicaciones, 'publicación', 'publicaciones')} de ${cuenta(r.productos, 'producto', 'productos')}.`,
      );
    } else {
      noSalio.push(` ${textoDeFrenoMl(r, false).replace(/^No publiqué nada/, 'En Mercado Libre NO se publicó')}`);
    }
  }

  // La pantalla abierta de este lanzamiento pasa a "Publicando" y sigue el avance sola.
  avisarCambioDeLanzamiento(detalle.id);

  if (salio.length === 0) {
    return texto(`No se publicó nada.${noSalio.join('')} Decíselo y ofrecé reintentar.`);
  }
  return texto(
    `Lo estoy publicando:${salio.join('')}${noSalio.join('')} Tarda un rato —van apareciendo de a poco en ${ambas ? 'cada tienda' : vaTn ? 'su panel de Tiendanube' : 'su Mercado Libre'}—, así que NO digas que ya está. Puede seguir el avance en la pantalla de Lanzamientos, con el estado de cada producto en cada tienda.`,
  );
}
